import {
  NoteArtifactsResponse,
  ExtendedNoteSourceArtifactInfo,
  SessionArtifactKind,
} from "@/components/note/editor/source-bar/types";
import { api } from "@/modules/api";
import { SourceKind } from "@/store/sources/types";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

export const useNoteSourceExtendedArtifactInfo = ({
  noteId,
  isOpen,
  sourceKind,
}: {
  noteId: string;
  isOpen: boolean;
  sourceKind: SourceKind;
}): ExtendedNoteSourceArtifactInfo => {
  /**
   * The Text Message artifacts are currently powered by
   * `useNoteSourceExtendedArtifactInfo`.
   *
   * In the future, we'll introduce a cleaner strategy for splitting
   * these up.
   */
  const isEnabled = useMemo(() => isOpen && sourceKind === "TEXT_MESSAGE", [isOpen, sourceKind]);

  const sessionArtifactKinds: SessionArtifactKind[] = useMemo(() => {
    const kinds: SessionArtifactKind[] = [];

    /**
     * We currently only use this for "text message" sources.
     */
    if (sourceKind === "TEXT_MESSAGE") {
      kinds.push("TEXT_MESSAGE");
    }

    return kinds;
  }, [sourceKind]);

  const queryFn = useCallback(async () => {
    const response = await api.get("/v2/notes/{note_id}/source-artifacts", {
      params: {
        path: { note_id: noteId },
        query: {
          artifact_kinds: sessionArtifactKinds,
        },
      },
    });

    if (response.error) {
      throw "Failed";
    }

    return response?.data;
  }, [noteId, sessionArtifactKinds]);

  const { data: result, isLoading } = useQuery({
    queryKey: ["note-source-extended-artifact-info", noteId],
    queryFn,
    enabled: isEnabled,
  });

  const artifacts = result?.artifacts as NoteArtifactsResponse | null;

  return {
    kind: sourceKind,
    data: artifacts,
    isLoading,
  };
};
